// Gemeinsame Bausteine der Auswertungs-Verträge. Geld in Cent, Mengen in Tausendstel der Basiseinheit des Artikels,
// Anteile und Veränderungen in Basispunkten (620 = 6,2 %), Tage als 'YYYY-MM-DD' (Europe/Berlin).
//
// Jede Auswertung ist eine lesende Aktion mit denselben Stellschrauben: Zeitraum (benannt oder von–bis), Vergleich,
// Standorte. Wer Standorte nennt, braucht das Recht an JEDEM davon; ohne Angabe gelten alle eigenen Standorte.
// Einstandswerte und Roherträge stehen nur mit `costs.read` in der Ausgabe – sonst fehlen die Felder ganz.
import { PERMISSION_COSTS_READ } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { COMPARE_MODES, PERIOD_PRESETS } from '../lib/period'

export const zDay = z.iso.date({ error: 'Bitte ein gültiges Datum eingeben.' })

/** Zeitraum: benannt (`period`) ODER von–bis (`from`, `to`); von–bis geht vor. Ohne Angabe gilt der Standard der Auswertung. */
export const periodFields = {
  period: z.enum(PERIOD_PRESETS, { error: 'Bitte einen Zeitraum aus der Liste wählen.' }).optional(),
  from: zDay.optional(),
  to: zDay.optional(),
}

export const compareField = { compare: z.enum(COMPARE_MODES, { error: 'Bitte einen Vergleich aus der Liste wählen.' }).default('previous') }

export const locationFields = {
  /** Standorte, auf die sich die Auswertung beschränkt; ohne Angabe alle Standorte, an denen das Recht gilt */
  locationIds: z.array(zId).max(50, { error: 'Höchstens 50 Standorte je Auswertung.' }).optional(),
}

export const csvField = {
  /** true: zusätzlich dieselben Zeilen als CSV-Datei (Semikolon, deutsches Zahlenformat) – für den Export der Oberfläche */
  withCsv: z.boolean().default(false),
}

/** Standortbezug aller Auswertungen: genannte Standorte müssen alle erlaubt sein, sonst filtert die Aktion selbst. */
export const reportScope = (input: { locationIds?: readonly string[] | undefined }): readonly string[] | 'any' =>
  input.locationIds && input.locationIds.length > 0 ? input.locationIds : 'any'

/** Filterwert „ohne“ – trifft Zeilen ohne Warengruppe, Kundengruppe, Kunde bzw. Artikel. */
export const NONE = 'none'
export const zIdOrNone = z.union([zId, z.literal(NONE)], { error: 'Bitte einen Eintrag aus der Liste wählen.' })

export const zPeriod = z.object({
  from: zDay,
  to: zDay,
  days: z.int(),
  /** „01.10.–13.10.2026“ */
  label: z.string(),
})

/** Woher die Zahlen stammen: verdichtete Tageswerte bis zum Stand, alles Jüngere laufend aus den Belegen. */
export const zDataState = z.object({
  /** letzter Lauf der Verdichtung; null = noch nie gelaufen */
  builtAt: z.date().nullable(),
  /** Tage des Zeitraums, die unmittelbar aus Belegen bzw. Journal gerechnet wurden (heute, noch nicht verdichtet, nachgebucht) */
  liveDays: z.int(),
  /** fertiger Satz: „Stand: heute 06:10 · heute laufend aus den Belegen“ */
  label: z.string(),
})

export const zCsv = z.object({ fileName: z.string(), content: z.string() })

export const zLocationRef = z.object({ id: zId, code: z.string(), name: z.string() })

/** Feldrechte: Einstandswerte und Roherträge an den genannten Stellen nur mit `costs.read`. */
export const costFields = (prefixes: readonly string[], fields: readonly string[]): Record<string, string> =>
  Object.fromEntries(prefixes.flatMap((prefix) => fields.map((field) => [prefix ? `${prefix}.${field}` : field, PERMISSION_COSTS_READ])))

export const READ_SALES = 'reporting.sales.read'
export const READ_STOCK = 'reporting.stock.read'
export const READ_PURCHASING = 'reporting.purchasing.read'
export const READ_FINANCE = 'reporting.finance.read'
export const READ_DASHBOARD = 'reporting.dashboard.read'
export const REBUILD = 'reporting.data.rebuild'
